import { AES, enc } from 'crypto-js'

const encryptionSecret = import.meta.env.VITE_JWT_SECRET_KEY // Replace with your own encryption secret

/**
 * Encrypts a JWT token.
 * @param token The JWT token to encrypt.
 * @returns The encrypted token.
 */
function encryptToken(token: string): string {
  return AES.encrypt(token, encryptionSecret).toString()
}

/**
 * Decrypts an encrypted JWT token.
 * @param encryptedToken The encrypted token to decrypt.
 * @returns The decrypted token.
 */
function decryptToken(encryptedToken: string): string {
  return AES.decrypt(encryptedToken, encryptionSecret).toString(enc.Utf8)
}

/**
 * Saves an encrypted JWT token to the local storage.
 * @param token The JWT token to save.
 * @param expireAt The JWT token expire time.
 */
function saveTokenToLocalStorage(token: string, expireAt: string): void {
  const encryptedToken = encryptToken(token)
  const encryptedExpireAt = encryptToken(expireAt)
  localStorage.setItem('ACCESS_TOKEN_ENCRYPTED', encryptedToken)
  localStorage.setItem('TOKEN_EXPIRE_AT_ENCRYPTED', encryptedExpireAt)
}

/**
 * Retrieves the decrypted JWT token from the local storage.
 * @returns The decrypted token if it exists, otherwise null.
 */
function getTokenFromLocalStorage(): string | null {
  const encryptedToken = localStorage.getItem('ACCESS_TOKEN_ENCRYPTED')

  if (!encryptedToken) return null

  try {
    const decryptedToken = decryptToken(encryptedToken)
    return decryptedToken
  } catch {
    removeTokenFromLocalStorage()

    return null
  }
}

/**
 * Removes the JWT token from the local storage.
 */
function removeTokenFromLocalStorage(): void {
  localStorage.removeItem('ACCESS_TOKEN_ENCRYPTED')
  localStorage.removeItem('TOKEN_EXPIRE_AT_ENCRYPTED')
}

export default {
  saveTokenToLocalStorage,
  getTokenFromLocalStorage,
  removeTokenFromLocalStorage
}
